import { CONFIG } from '../config.js';
import { identifyUser, trackPurchase } from './backend.js';
import { refreshUrlTriggeredSurveys } from '../initialize.js';
import { getStoredUTMParameters } from '../utils/utm.js';

export function initializePH1API(storage, projectId) {
    window.PH1 = window.PH1 || {};
    window.PH1 = {
        identify: async function (identifier = null, metadata = {}) {
            var userId = storage.get('ph_user_id');

            // Clear cached attributes on identify
            const cacheKey = `ph_attributes_${projectId}_${userId}`;
            sessionStorage.removeItem(cacheKey);

            var ablyftData = {};
            if (metadata.ablyft) {
                ablyftData = metadata.ablyft;
                delete metadata.ablyft;
            }

            await identifyUser(CONFIG.apiUrl, {
                project_id: projectId,
                user_id: userId,
                identifier,
                metadata,
                ablyft: ablyftData,
            });

            refreshUrlTriggeredSurveys();
        },

        trackPurchase: async function (data) {
            if (!data || !data.transaction_id || !data.value) {
                return;
            }

            const now = Date.now();
            const maxAge = 60 * 60 * 1000; // 60 minutes

            let ordersRaw = storage.get('ph_order_ids');
            let orders = [];
            if (ordersRaw) {
                try {
                    orders = typeof ordersRaw === 'string' ? JSON.parse(ordersRaw) : ordersRaw;
                    if (!Array.isArray(orders)) orders = [];
                } catch (e) {
                    orders = [];
                }
            }

            orders = orders.filter((order) => now - order.timestamp < maxAge);
            if (orders.find((order) => order.id === data.transaction_id)) {
                return;
            }
            orders.push({ id: data.transaction_id, timestamp: now });
            storage.set('ph_order_ids', JSON.stringify(orders));

            const userId = storage.get('ph_user_id');

            // Clear cached attributes before refresh
            const cacheKey = `ph_attributes_${projectId}_${userId}`;
            sessionStorage.removeItem(cacheKey);

            const utmParams = getStoredUTMParameters(storage);

            try {
                await trackPurchase(CONFIG.apiUrl, {
                    transaction_id: data.transaction_id,
                    amount: data.value,
                    currency: data.currency || 'EUR',
                    tax: data.tax || 0,
                    items: data.items || [],
                    user_id: userId,
                    project_id: projectId,
                    ...utmParams,
                    coupon: data.coupon || null,
                });
            } catch {
                // The local recheck below must run even when the request failed
            }

            // Re-evaluate the URL surveys with the purchase applied locally, so
            // eCommerce rules (totalRevenue, ordersCount, SKUs ...) react right
            // away even when the backend has not aggregated the order yet.
            await refreshUrlTriggeredSurveys({
                purchase: {
                    value: data.value,
                    coupon: data.coupon || null,
                    items: data.items || [],
                },
            });
        },
    };
}

export function setupPHAPIQueue(storage, projectId) {
    window.PH_API = window.PH_API || [];

    const handleQueue = () => {
        while (window.PH_API.length > 0) {
            const f = window.PH_API.shift();

            if (typeof f === 'function') {
                try {
                    f();
                } catch (error) {
                    // Dev build only: the production build strips console calls
                    console.warn('[PollHero] PH_API callback failed:', error);
                }
            } else if (typeof f === 'object' && f.event === 'purchase') {
                window.PH1.trackPurchase(f);
            } else if (typeof f === 'object' && f.event === 'identify') {
                // Documented shape: { event: 'identify', identifier, metadata }.
                // An object without these fields is passed through unchanged
                // (legacy integrations that relied on the old behaviour).
                if ('identifier' in f || 'metadata' in f) {
                    window.PH1.identify(f.identifier ?? null, f.metadata ?? {});
                } else {
                    window.PH1.identify(f);
                }
            }
        }
    };

    const phOriginalPush = window.PH_API.push.bind(window.PH_API);

    window.PH_API.push = function (fn) {
        const result = phOriginalPush(fn);
        handleQueue();
        return result;
    };

    return handleQueue;
}
